import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { fetchAPI } from "../lib/utils";
import {
	DEFAULT_EXERCISE_CATEGORIES,
	emptyConditioningTemplate,
	isConditioningCategory,
	normalizeCategoryValue,
	normalizeConditioningRounds,
	getCategoryColor,
	getImageUrl,
	getYouTubeThumbnail,
	getYouTubeEmbedUrl,
} from "../lib/coachUtils";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	DialogDescription,
} from "./ui/dialog";
import {
	Plus,
	Search,
	Trash2,
	Edit,
	Video,
	Image as ImageIcon,
	Play,
	Upload,
	Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import {
	ConditioningTemplateEditor,
	CONDITIONING_TYPES,
} from "./ConditioningTemplateEditor";
import { ExerciseVideoDialog } from "./ExerciseVideoDialog";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export function ExercisesTab() {
	const { t, language } = useLanguage();
	const { user, token } = useAuth();
	const location = useLocation();

	// ── State ──
	const [loading, setLoading] = useState(false);
	const [exercises, setExercises] = useState([]);
	const [exerciseSearch, setExerciseSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("all");
	const [libraryScope, setLibraryScope] = useState("mine");
	const [adminOwners, setAdminOwners] = useState([]);
	const [selectedOwnerId, setSelectedOwnerId] = useState("all");
	const [isCreateExerciseOpen, setIsCreateExerciseOpen] = useState(false);
	const [isEditExerciseOpen, setIsEditExerciseOpen] = useState(false);
	const [selectedExercise, setSelectedExercise] = useState(null);
	const [videoExercise, setVideoExercise] = useState(null);
	const [exerciseLinks, setExerciseLinks] = useState(null);
	const [isExerciseLinksLoading, setIsExerciseLinksLoading] = useState(false);
	const [showProgramLinkDetails, setShowProgramLinkDetails] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [exerciseCategories, setExerciseCategories] = useState(
		DEFAULT_EXERCISE_CATEGORIES,
	);
	const [loadReferenceOptions, setLoadReferenceOptions] = useState([]);
	const createExerciseFormState = () => ({
		name: "",
		description: "",
		video_url: "",
		category: "strength",
		tags: "",
		library_scope: user?.role === "admin" ? "personal" : undefined,
		load_reference_exercise_id: null,
		load_reference_exercise_name: null,
	});
	const [newExercise, setNewExercise] = useState(createExerciseFormState);
	const [newConditioning, setNewConditioning] = useState(
		emptyConditioningTemplate(),
	);
	const [editingCondExercise, setEditingCondExercise] = useState(null);

	const EXERCISE_CATEGORIES = exerciseCategories;
	const currentUserId = String(user?.user_id || "");
	const normalizeScope = (value) => {
		const text = String(value || "")
			.trim()
			.toLowerCase();
		return text === "personal" ? "personal" : "global";
	};
	const ownerFor = (item) =>
		String(item?.owner_id || item?.coach_id || item?.created_by || "");
	const applyClientScopeFilter = (items, scope, ownerId = "all") => {
		const list = Array.isArray(items) ? items : [];
		const filteredByOwner =
			user?.role === "admin" && scope === "all" && ownerId !== "all"
				? list.filter(
						(item) =>
							ownerFor(item) === ownerId &&
							normalizeScope(item?.library_scope) === "personal",
					)
				: list;
		if (scope === "global") {
			return filteredByOwner.filter(
				(item) => normalizeScope(item?.library_scope) === "global",
			);
		}
		if (scope === "mine") {
			return filteredByOwner.filter(
				(item) =>
					normalizeScope(item?.library_scope) === "personal" &&
					ownerFor(item) === currentUserId,
			);
		}
		if (scope === "visible") {
			return filteredByOwner.filter((item) => {
				const tag = normalizeScope(item?.library_scope);
				return tag === "global" || ownerFor(item) === currentUserId;
			});
		}
		if (scope === "all") {
			return filteredByOwner;
		}
		return filteredByOwner;
	};
	const ownerQuery =
		user?.role === "admin" &&
		libraryScope === "all" &&
		selectedOwnerId !== "all"
			? `&owner_id=${encodeURIComponent(selectedOwnerId)}`
			: "";
	const getCategoryLabel = (cat) => {
		const key = typeof cat === "string" ? cat : cat?.value;
		const fallback =
			typeof cat === "string" ? cat : cat?.label || cat?.name || key || "";
		if (!key) return fallback;
		const translated = t(key);
		return translated && translated !== key ? translated : fallback;
	};
	const getScopeBadgeClass = (scope) =>
		normalizeScope(scope) === "global"
			? "bg-indigo-500/20 text-indigo-200"
			: "bg-emerald-600/20 text-emerald-200";
	const ownerLabelFor = (ownerId) => {
		const match = adminOwners.find(
			(owner) => owner.value === String(ownerId || ""),
		);
		return match?.label || String(ownerId || "");
	};
	const resolveLoadReferenceSelection = (referenceId) => {
		if (referenceId === "__bodyweight__") {
			return {
				load_reference_exercise_id: "__bodyweight__",
				load_reference_exercise_name: t("athleteBodyweightReference"),
			};
		}
		if (!referenceId || referenceId === "__current_exercise__") {
			return {
				load_reference_exercise_id: null,
				load_reference_exercise_name: null,
			};
		}
		const selectedReference = loadReferenceOptions.find(
			(option) => option.exercise_id === referenceId,
		);
		return {
			load_reference_exercise_id: selectedReference?.exercise_id || referenceId,
			load_reference_exercise_name:
				selectedReference?.name || selectedReference?.exercise_name || null,
		};
	};

	// ── Data loading ──
	useEffect(() => {
		loadInitialData();
	}, []);

	useEffect(() => {
		loadExercises();
	}, [categoryFilter, libraryScope, selectedOwnerId]);

	useEffect(() => {
		if (user?.role !== "admin") return;
		const loadOwners = async () => {
			try {
				const payload = await fetchAPI("/admin/coaches-with-athletes");
				const owners = Array.isArray(payload)
					? payload
					: Array.isArray(payload?.coaches)
						? payload.coaches
						: [];
				const options = owners.map((coach) => ({
					value: coach.user_id,
					label: coach.name || coach.email || coach.user_id,
				}));
				setAdminOwners(options);
			} catch (error) {
				console.error("Failed to load admin owners:", error);
			}
		};
		loadOwners();
	}, [user?.role]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		if (params.get("from") !== "dashboard") return;

		const rawScope = String(params.get("scope") || "")
			.trim()
			.toLowerCase();
		const rawCategory = String(params.get("category") || "")
			.trim()
			.toLowerCase();

		if (rawScope === "personal" || rawScope === "mine") {
			setLibraryScope("mine");
			setSelectedOwnerId("all");
		} else if (rawScope === "global") {
			setLibraryScope("global");
			setSelectedOwnerId("all");
		}

		if (rawCategory === "conditioning") {
			setCategoryFilter("conditioning");
		} else if (rawCategory === "all") {
			setCategoryFilter("all");
		}
	}, [location.search]);

	const loadInitialData = async () => {
		try {
			const scopeQuery = `?scope=${encodeURIComponent(libraryScope)}${ownerQuery}`;
			const [exercisesData, categoriesData, loadReferenceOptionsData] =
				await Promise.all([
					fetchAPI(`/exercises${scopeQuery}`).catch(() => []),
					fetchAPI("/exercises/categories").catch(() => []),
					fetchAPI("/exercises/load-reference-options").catch(() => []),
				]);
			setExercises(
				applyClientScopeFilter(
					exercisesData || [],
					libraryScope,
					selectedOwnerId,
				),
			);
			setLoadReferenceOptions(
				Array.isArray(loadReferenceOptionsData) ? loadReferenceOptionsData : [],
			);
			if (Array.isArray(categoriesData) && categoriesData.length > 0) {
				const normalizedCats = categoriesData.map((c) => ({
					...c,
					value:
						typeof c.value === "string"
							? c.value.trim().toLowerCase()
							: (c.name || c.label || "").trim().toLowerCase(),
					label: c.label || c.name || c.value || "",
				}));
				const seen = new Set();
				const dedupedCats = normalizedCats.filter((c) => {
					if (seen.has(c.value)) return false;
					seen.add(c.value);
					return true;
				});
				const hasConditioning = dedupedCats.some(
					(c) => c.value === "conditioning",
				);
				if (!hasConditioning) {
					dedupedCats.push({ value: "conditioning", label: "Conditioning" });
				}
				setExerciseCategories(dedupedCats);
			}
		} catch (error) {
			console.error("Failed to load exercises data:", error);
		} finally {
			setLoading(false);
		}
	};

	const loadExercises = async () => {
		setLoading(true);
		try {
			const isCondFilter = isConditioningCategory(categoryFilter);
			const isAll = categoryFilter === "all";
			const scopeQuery = `?scope=${encodeURIComponent(libraryScope)}${ownerQuery}`;

			if (isCondFilter) {
				const templates = await fetchAPI(
					`/conditioning-library${scopeQuery}`,
				).catch((err) => {
					console.error(
						"[ExercisesTab] Failed to load conditioning templates:",
						err,
					);
					return [];
				});
				const normalized = (templates || []).map((tpl) => ({
					...tpl,
					exercise_id: tpl.conditioning_id,
					name: tpl.name,
					category: "conditioning",
					_isConditioning: true,
				}));
				setExercises(
					applyClientScopeFilter(normalized, libraryScope, selectedOwnerId),
				);
			} else if (isAll) {
				const regularData = await fetchAPI(`/exercises${scopeQuery}`).catch(
					(err) => {
						console.error("[ExercisesTab] Failed to load exercises:", err);
						return [];
					},
				);
				setExercises(
					applyClientScopeFilter(
						regularData || [],
						libraryScope,
						selectedOwnerId,
					),
				);
			} else {
				const params = `?category=${encodeURIComponent(categoryFilter)}&scope=${encodeURIComponent(libraryScope)}${ownerQuery}`;
				const data = await fetchAPI(`/exercises${params}`);
				setExercises(
					applyClientScopeFilter(data || [], libraryScope, selectedOwnerId),
				);
			}
		} catch (error) {
			console.error("Failed to load exercises:", error);
		} finally {
			setLoading(false);
		}
	};

	// ── Handlers ──
	const handleCreateExercise = async (e) => {
		e.preventDefault();
		try {
			if (newExercise.category === "conditioning") {
				if (!newConditioning.name.trim()) {
					toast.error("Inserisci il nome del circuito");
					return;
				}
				if (!newConditioning.conditioning_type) {
					toast.error("Seleziona il tipo WOD");
					return;
				}

				await fetchAPI("/conditioning-library", {
					method: "POST",
					body: JSON.stringify({
						name: newConditioning.name.trim(),
						conditioning_type: newConditioning.conditioning_type,
						time_cap_seconds: newConditioning.time_cap_seconds
							? Number(newConditioning.time_cap_seconds)
							: null,
						rounds: normalizeConditioningRounds(newConditioning.rounds),
						interval_seconds: newConditioning.interval_seconds
							? Number(newConditioning.interval_seconds)
							: null,
						conditioning_exercises: (
							newConditioning.conditioning_exercises || []
						).filter((ce) => ce.exercise_name),
						conditioning_free_text:
							newConditioning.conditioning_free_text || null,
						emom_rounds: newConditioning.emom_rounds?.length
							? newConditioning.emom_rounds
							: null,
						emom_conditioning_interval:
							newConditioning.emom_conditioning_interval || null,
						notes: newConditioning.notes || null,
						library_scope:
							user?.role === "admin"
								? newExercise.library_scope || "personal"
								: "personal",
						load_reference_exercise_id:
							newExercise.load_reference_exercise_id || null,
						load_reference_exercise_name:
							newExercise.load_reference_exercise_name || null,
					}),
				});

				toast.success("Circuito conditioning creato con successo");
				setIsCreateExerciseOpen(false);
				setNewExercise(createExerciseFormState());
				setNewConditioning(emptyConditioningTemplate());
				loadExercises();
				return;
			}

			const exerciseData = {
				...newExercise,
				tags: newExercise.tags
					.split(",")
					.map((t) => t.trim())
					.filter((t) => t),
			};
			await fetchAPI("/exercises", {
				method: "POST",
				body: JSON.stringify(exerciseData),
			});
			toast.success("Esercizio creato con successo");
			setIsCreateExerciseOpen(false);
			setNewExercise(createExerciseFormState());
			setNewConditioning(emptyConditioningTemplate());
			loadExercises();
		} catch (error) {
			toast.error("Errore nella creazione: " + error.message);
		}
	};

	const handleUpdateExercise = async (e) => {
		e.preventDefault();
		if (!selectedExercise) return;
		try {
			await fetchAPI(`/exercises/${selectedExercise.exercise_id}`, {
				method: "PUT",
				body: JSON.stringify({
					name: selectedExercise.name,
					description: selectedExercise.description,
					video_url: selectedExercise.video_url,
					category: selectedExercise.category,
					tags:
						typeof selectedExercise.tags === "string"
							? selectedExercise.tags
									.split(",")
									.map((t) => t.trim())
									.filter((t) => t)
							: selectedExercise.tags,
					load_reference_exercise_id:
						selectedExercise.load_reference_exercise_id || null,
					load_reference_exercise_name:
						selectedExercise.load_reference_exercise_name || null,
				}),
			});
			toast.success("Esercizio aggiornato");
			setIsEditExerciseOpen(false);
			loadExercises();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleUpdateConditioning = async (e) => {
		e.preventDefault();
		if (!editingCondExercise) return;
		try {
			if (!newConditioning.name.trim()) {
				toast.error("Inserisci il nome del circuito");
				return;
			}
			if (!newConditioning.conditioning_type) {
				toast.error("Seleziona il tipo WOD");
				return;
			}
			await fetchAPI(
				`/conditioning-library/${editingCondExercise.conditioning_id}`,
				{
					method: "PUT",
					body: JSON.stringify({
						name: newConditioning.name.trim(),
						conditioning_type: newConditioning.conditioning_type,
						time_cap_seconds: newConditioning.time_cap_seconds
							? Number(newConditioning.time_cap_seconds)
							: null,
						rounds: normalizeConditioningRounds(newConditioning.rounds),
						interval_seconds: newConditioning.interval_seconds
							? Number(newConditioning.interval_seconds)
							: null,
						conditioning_exercises: (
							newConditioning.conditioning_exercises || []
						).filter((ce) => ce.exercise_name),
						conditioning_free_text:
							newConditioning.conditioning_free_text || null,
						emom_rounds: newConditioning.emom_rounds?.length
							? newConditioning.emom_rounds
							: null,
						emom_conditioning_interval:
							newConditioning.emom_conditioning_interval || null,
						notes: newConditioning.notes || null,
						result_schema: newConditioning.result_schema || [],
					}),
				},
			);
			toast.success("Circuito conditioning aggiornato");
			setEditingCondExercise(null);
			setNewConditioning(emptyConditioningTemplate());
			setNewExercise(createExerciseFormState());
			loadExercises();
		} catch (error) {
			const msg = String(error?.detail || error?.message || "").toLowerCase();
			if (
				error?.status === 409 ||
				msg.includes("schema risultato bloccato") ||
				msg.includes("risultati inseriti")
			) {
				toast.error(
					"Lo schema risultato ha già risultati inseriti. Per cambiarlo e convertire i dati, modifica questo conditioning dalla pagina Conditioning (pulsante 'Converti e sblocca').",
				);
			} else {
				toast.error("Errore nell'aggiornamento: " + error.message);
			}
		}
	};

	const handleImageUpload = async (exerciseId, file) => {
		if (!file) return;
		setIsUploading(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			const response = await fetch(
				`${backendUrl}/api/exercises/${exerciseId}/image`,
				{
					method: "POST",
					credentials: "include",
					headers: {
						Authorization: `Bearer ${
							localStorage.getItem("session_token") || ""
						}`,
					},
					body: formData,
				},
			);
			if (!response.ok) {
				const error = await response.json();
				throw new Error(error.detail || "Upload failed");
			}
			toast.success("Immagine caricata");
			loadExercises();
			if (selectedExercise?.exercise_id === exerciseId) {
				const result = await response.json();
				setSelectedExercise((prev) => ({
					...prev,
					image_url: result.image_url,
				}));
			}
		} catch (error) {
			toast.error("Errore upload: " + error.message);
		} finally {
			setIsUploading(false);
		}
	};

	const handleDeleteImage = async (exerciseId) => {
		try {
			await fetchAPI(`/exercises/${exerciseId}/image`, { method: "DELETE" });
			toast.success("Immagine rimossa");
			loadExercises();
			if (selectedExercise?.exercise_id === exerciseId) {
				setSelectedExercise((prev) => ({ ...prev, image_url: null }));
			}
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const handleDeleteExercise = async (exerciseId) => {
		if (!window.confirm("Sei sicuro di voler eliminare questo esercizio?"))
			return;
		try {
			const exercise = exercises.find((ex) => ex.exercise_id === exerciseId);
			if (exercise && exercise._isConditioning) {
				await fetchAPI(
					`/conditioning-library/${exercise.conditioning_id || exerciseId}`,
					{ method: "DELETE" },
				);
			} else {
				await fetchAPI(`/exercises/${exerciseId}`, { method: "DELETE" });
			}
			toast.success("Esercizio eliminato");
			loadExercises();
		} catch (error) {
			toast.error("Errore: " + error.message);
		}
	};

	const loadExerciseLinks = async (exerciseId) => {
		if (!exerciseId) {
			setExerciseLinks(null);
			setShowProgramLinkDetails(false);
			return;
		}
		setIsExerciseLinksLoading(true);
		setShowProgramLinkDetails(false);
		try {
			const links = await fetchAPI(`/exercises/${exerciseId}/links`);
			setExerciseLinks(links || null);
		} catch (error) {
			console.error("Failed to load exercise links:", error);
			setExerciseLinks(null);
		} finally {
			setIsExerciseLinksLoading(false);
		}
	};

	const openEditExercise = (exercise) => {
		if (exercise._isConditioning) {
			setNewExercise((prev) => ({ ...prev, category: "conditioning" }));
			setNewConditioning({
				name: exercise.name || "",
				conditioning_type: exercise.conditioning_type || "",
				time_cap_seconds: exercise.time_cap_seconds || null,
				rounds: exercise.rounds || null,
				interval_seconds: exercise.interval_seconds || null,
				conditioning_exercises: (exercise.conditioning_exercises || []).map(
					(ce) => ({
						exercise_id: ce.exercise_id || "",
						exercise_name: ce.exercise_name || "",
						reps: ce.reps || "",
						load: ce.load || "",
						notes: ce.notes || "",
					}),
				),
				conditioning_free_text: exercise.conditioning_free_text || "",
				emom_rounds: exercise.emom_rounds || [],
				emom_conditioning_interval: exercise.emom_conditioning_interval || "",
				notes: exercise.notes || "",
			});
			setEditingCondExercise(exercise);
		} else {
			setSelectedExercise({
				...exercise,
				tags: Array.isArray(exercise.tags)
					? exercise.tags.join(", ")
					: exercise.tags || "",
			});
			setExerciseLinks(null);
			setShowProgramLinkDetails(false);
			setIsEditExerciseOpen(true);
			loadExerciseLinks(exercise.exercise_id);
		}
	};

	const filteredExercises = exercises.filter((ex) =>
		ex.name?.toLowerCase().includes(exerciseSearch.toLowerCase()),
	);

	const isConditioning = isConditioningCategory(newExercise.category);

	// ── Render ──

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-[60vh]">
				<div className="spinner"></div>
			</div>
		);
	}

	return (
		<div
			className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8"
			data-testid="exercises-tab"
		>
			<div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
				<div>
					<h2 className="font-heading text-2xl">{t("exercises")}</h2>
					<p className="text-sm text-muted-foreground">
						{t("manageExercises")}
					</p>
				</div>
				<Dialog
					open={isCreateExerciseOpen}
					onOpenChange={(open) => {
						setIsCreateExerciseOpen(open);
						if (!open) {
							setNewExercise(createExerciseFormState());
							setNewConditioning(emptyConditioningTemplate());
						}
					}}
				>
					<DialogTrigger asChild>
						<Button
							className="bg-volt hover:bg-volt/90 rounded-sm"
							data-testid="new-exercise-btn"
						>
							<Plus className="w-4 h-4 mr-2" />
							{t("newExercise")}
						</Button>
					</DialogTrigger>
					<DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
						<DialogDescription className="hidden" />
						<DialogHeader>
							<DialogTitle className="font-heading">
								{t("newExercise")}
							</DialogTitle>
						</DialogHeader>
						<form onSubmit={handleCreateExercise} className="space-y-4">
							{/* Category Selector */}
							<div className="space-y-2">
								<Label className="label-tactical">{t("category")}</Label>
								<Select
									value={newExercise.category}
									onValueChange={(value) =>
										setNewExercise({ ...newExercise, category: value })
									}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										{EXERCISE_CATEGORIES.map((cat) => (
											<SelectItem key={cat.value} value={cat.value}>
												{getCategoryLabel(cat)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							{user?.role === "admin" && (
								<div className="space-y-2">
									<Label className="label-tactical">{t("libraryScope")}</Label>
									<Select
										value={newExercise.library_scope || "personal"}
										onValueChange={(value) =>
											setNewExercise({ ...newExercise, library_scope: value })
										}
									>
										<SelectTrigger className="bg-secondary border-transparent rounded-sm">
											<SelectValue />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem value="personal">
												{t("scopePersonalLabel")}
											</SelectItem>
											<SelectItem value="global">
												{t("scopeGlobalLabel")}
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}

							{isConditioning ? (
								<ConditioningTemplateEditor
									value={newConditioning}
									setValue={setNewConditioning}
									isAdmin={user?.role === "admin"}
								/>
							) : (
								<>
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("exerciseName")} *
										</Label>
										<Input
											value={newExercise.name}
											onChange={(e) =>
												setNewExercise({
													...newExercise,
													name: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="es. Squat, Bench Press"
											required
										/>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">{t("description")}</Label>
										<Textarea
											value={newExercise.description}
											onChange={(e) =>
												setNewExercise({
													...newExercise,
													description: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm min-h-[80px]"
											placeholder="Descrizione dell'esercizio..."
										/>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">{t("videoUrl")}</Label>
										<Input
											value={newExercise.video_url}
											onChange={(e) =>
												setNewExercise({
													...newExercise,
													video_url: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="https://youtube.com/watch?v=..."
										/>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">{t("tags")}</Label>
										<Input
											value={newExercise.tags}
											onChange={(e) =>
												setNewExercise({
													...newExercise,
													tags: e.target.value,
												})
											}
											className="bg-secondary border-transparent rounded-sm"
											placeholder="upper, push, compound"
										/>
									</div>
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("loadReferenceExercise")}
										</Label>
										<Select
											value={
												newExercise.load_reference_exercise_id ||
												"__current_exercise__"
											}
											onValueChange={(value) =>
												setNewExercise({
													...newExercise,
													...resolveLoadReferenceSelection(value),
												})
											}
										>
											<SelectTrigger className="bg-secondary border-transparent rounded-sm">
												<SelectValue
													placeholder={t("currentExerciseLoadReference")}
												/>
											</SelectTrigger>
											<SelectContent className="bg-card border-border max-h-72">
												<SelectItem value="__current_exercise__">
													{t("currentExerciseLoadReference")}
												</SelectItem>
												<SelectItem value="__bodyweight__">
													{t("athleteBodyweightReference")}
												</SelectItem>
												{loadReferenceOptions.map((option) => (
													<SelectItem
														key={option.exercise_id}
														value={option.exercise_id}
													>
														{option.name || option.exercise_name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</>
							)}
							<Button
								type="submit"
								className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							>
								{isConditioning ? t("createConditioning") : t("createExercise")}
							</Button>
						</form>
					</DialogContent>
				</Dialog>
			</div>

			{/* Filters */}
			<div className="flex flex-col sm:flex-row gap-3 mb-6">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
					<Input
						placeholder={t("searchExercises")}
						value={exerciseSearch}
						onChange={(e) => setExerciseSearch(e.target.value)}
						className="pl-10 bg-secondary border-transparent h-12 rounded-sm"
						data-testid="exercise-search"
					/>
				</div>
				<Select value={categoryFilter} onValueChange={setCategoryFilter}>
					<SelectTrigger className="bg-secondary border-transparent rounded-sm w-full sm:w-[200px]">
						<SelectValue placeholder={t("allCategories")} />
					</SelectTrigger>
					<SelectContent className="bg-card border-border">
						<SelectItem value="all">{t("allCategories")}</SelectItem>
						{EXERCISE_CATEGORIES.map((cat) => (
							<SelectItem key={cat.value} value={cat.value}>
								{getCategoryLabel(cat)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<div className="flex flex-wrap gap-2">
					<Button
						type="button"
						variant={libraryScope === "mine" ? "default" : "outline"}
						onClick={() => setLibraryScope("mine")}
						className="rounded-sm"
					>
						{t("libraryScopePersonalPlural")}
					</Button>
					<Button
						type="button"
						variant={libraryScope === "global" ? "default" : "outline"}
						onClick={() => setLibraryScope("global")}
						className="rounded-sm"
					>
						{t("libraryScopeGlobalPlural")}
					</Button>
					<Button
						type="button"
						variant={libraryScope === "visible" ? "default" : "outline"}
						onClick={() => setLibraryScope("visible")}
						className="rounded-sm"
					>
						{t("libraryScopeVisible")}
					</Button>
					{user?.role === "admin" && (
						<Button
							type="button"
							variant={libraryScope === "all" ? "default" : "outline"}
							onClick={() => setLibraryScope("all")}
							className="rounded-sm"
						>
							{t("libraryScopeAllAdmin")}
						</Button>
					)}
				</div>
				{user?.role === "admin" && libraryScope === "all" && (
					<Select value={selectedOwnerId} onValueChange={setSelectedOwnerId}>
						<SelectTrigger className="bg-secondary border-transparent rounded-sm w-full sm:w-[220px]">
							<SelectValue placeholder={t("ownerFilterLabel")} />
						</SelectTrigger>
						<SelectContent className="bg-card border-border">
							<SelectItem value="all">{t("allOwners")}</SelectItem>
							{adminOwners.map((owner) => (
								<SelectItem key={owner.value} value={owner.value}>
									{owner.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</div>

			<ExerciseVideoDialog
				videoUrl={videoExercise?.video_url}
				exerciseName={videoExercise?.name}
				open={Boolean(videoExercise)}
				onOpenChange={(open) => {
					if (!open) setVideoExercise(null);
				}}
			/>

			{/* Exercise Grid */}
			{filteredExercises.length > 0 ? (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{filteredExercises.map((exercise) => {
						const isCond = exercise._isConditioning;
						const isGlobalReadOnly =
							exercise.library_scope === "global" && user?.role !== "admin";
						const thumbUrl = getYouTubeThumbnail(exercise.video_url);
						const imgUrl = getImageUrl(exercise.image_url);

						return (
							<Card
								key={exercise.exercise_id}
								className="bg-card border-border rounded-sm hover:border-volt/30 transition-colors"
								data-testid={`exercise-card-${exercise.exercise_id}`}
							>
								<CardContent className="p-0">
									{/* Media preview */}
									{(thumbUrl || imgUrl) && (
										<div className="relative aspect-video bg-secondary overflow-hidden">
											{thumbUrl ? (
												<>
													<img
														src={thumbUrl}
														alt={exercise.name}
														className="w-full h-full object-cover"
													/>
													<button
														type="button"
														aria-label={t("videoPreview")}
														className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
														onClick={() => setVideoExercise(exercise)}
													>
														<Play className="w-8 h-8 text-white" />
													</button>
												</>
											) : (
												<img
													src={imgUrl}
													alt={exercise.name}
													className="w-full h-full object-cover"
												/>
											)}
										</div>
									)}
									<div className="p-4">
										<div className="flex items-start justify-between mb-2">
											<div className="flex-1">
												<p className="font-heading text-base mb-1">
													{exercise.name}
												</p>
												<div className="flex items-center gap-2 flex-wrap">
													<Badge
														className={`${getCategoryColor(
															exercise.category,
														)} border-0 rounded-sm`}
													>
														{getCategoryLabel(exercise.category)}
													</Badge>
													<Badge
														className={`border-0 rounded-sm text-[10px] ${getScopeBadgeClass(
															exercise.library_scope,
														)}`}
													>
														{exercise.library_scope === "global"
															? t("libraryScopeGlobalSingle")
															: t("libraryScopePersonal")}
													</Badge>
													{user?.role === "admin" &&
														libraryScope === "all" &&
														normalizeScope(exercise.library_scope) ===
															"personal" && (
															<Badge className="border-0 rounded-sm text-[10px] bg-slate-600/25 text-slate-200">
																{ownerLabelFor(
																	exercise.owner_id || exercise.coach_id,
																)}
															</Badge>
														)}
													{isCond && exercise.conditioning_type && (
														<Badge className="bg-yellow-500/20 text-yellow-400 border-0 rounded-sm">
															<Zap className="w-3 h-3 mr-1" />
															{exercise.conditioning_type.toUpperCase()}
														</Badge>
													)}
												</div>
											</div>
											<div className="flex gap-1">
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-8 w-8"
													disabled={isGlobalReadOnly}
													title={
														isGlobalReadOnly
															? t("globalExerciseEditAdminOnly")
															: undefined
													}
													onClick={() => openEditExercise(exercise)}
												>
													<Edit className="w-4 h-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="rounded-sm h-8 w-8 text-destructive hover:text-destructive"
													disabled={isGlobalReadOnly}
													title={
														isGlobalReadOnly
															? t("globalExerciseDeleteAdminOnly")
															: undefined
													}
													onClick={() =>
														handleDeleteExercise(exercise.exercise_id)
													}
												>
													<Trash2 className="w-4 h-4" />
												</Button>
											</div>
										</div>
										{exercise.description && (
											<p className="text-sm text-muted-foreground line-clamp-2">
												{exercise.description}
											</p>
										)}
										{/* Media indicators */}
										<div className="flex items-center gap-2 mt-2">
											{exercise.video_url && (
												<a
													href={exercise.video_url}
													target="_blank"
													rel="noopener noreferrer"
													className="text-muted-foreground hover:text-volt"
												>
													<Video className="w-4 h-4" />
												</a>
											)}
											{exercise.image_url && (
												<ImageIcon className="w-4 h-4 text-muted-foreground" />
											)}
											{!isCond && (
												<label className="cursor-pointer text-muted-foreground hover:text-volt">
													<Upload className="w-4 h-4" />
													<input
														type="file"
														accept="image/*"
														className="hidden"
														onChange={(e) =>
															handleImageUpload(
																exercise.exercise_id,
																e.target.files[0],
															)
														}
													/>
												</label>
											)}
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>
			) : (
				<Card className="bg-card border-border rounded-sm">
					<CardContent className="flex flex-col items-center justify-center py-12 text-center">
						<Search className="w-12 h-12 text-muted-foreground mb-4" />
						<p className="font-heading text-lg mb-2">{t("noExercisesFound")}</p>
					</CardContent>
				</Card>
			)}

			{/* Edit Exercise Dialog (regular) */}
			<Dialog
				open={isEditExerciseOpen}
				onOpenChange={(open) => {
					setIsEditExerciseOpen(open);
					if (!open) {
						setExerciseLinks(null);
						setShowProgramLinkDetails(false);
					}
				}}
			>
				<DialogContent className="bg-card border-border w-[calc(100vw-2rem)] max-w-lg max-h-[88vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("editExercise")}
						</DialogTitle>
					</DialogHeader>
					{selectedExercise && (
						<form onSubmit={handleUpdateExercise} className="space-y-4">
							<div className="space-y-2 rounded-sm border border-border bg-secondary/30 p-3">
								<p className="label-tactical">{t("exerciseLinksTitle")}</p>
								<p className="text-xs text-muted-foreground">
									{t("exerciseLinksHint")}
								</p>
								{isExerciseLinksLoading ? (
									<p className="text-xs text-muted-foreground">
										{t("loading")}
									</p>
								) : (
									<div className="space-y-3">
										<div className="space-y-1">
											<p className="text-xs uppercase tracking-wider text-muted-foreground">
												{t("linkedPrograms")}
											</p>
											{(exerciseLinks?.programs || []).length > 0 ? (
												<>
													<div className="flex items-center gap-2 text-xs">
														<Badge className="rounded-sm border-0 bg-volt/15 text-volt">
															{exerciseLinks?.counts?.linked_programs ||
																exerciseLinks.programs.length}
														</Badge>
														<span className="text-muted-foreground">
															{t("linkedActiveProgramsLabel")}
														</span>
														<button
															type="button"
															onClick={() =>
																setShowProgramLinkDetails((prev) => !prev)
															}
															className="ml-auto text-[11px] text-volt hover:text-volt/80"
														>
															{showProgramLinkDetails
																? t("hideLinkedProgramDetails")
																: t("showLinkedProgramDetails")}
														</button>
													</div>
													{showProgramLinkDetails && (
														<div className="space-y-1">
															{exerciseLinks.programs.map((program) => (
																<div
																	key={program.program_id}
																	className="flex items-center justify-between gap-2 text-xs"
																>
																	<span className="truncate">
																		{program.program_title}
																	</span>
																	<Badge className="rounded-sm border-0 bg-volt/15 text-volt">
																		{program.occurrences}{" "}
																		{t("linkedOccurrences")}
																	</Badge>
																</div>
															))}
														</div>
													)}
												</>
											) : (
												<p className="text-xs text-muted-foreground">
													{t("noProgramLinks")}
												</p>
											)}
										</div>

										<div className="space-y-1">
											<p className="text-xs uppercase tracking-wider text-muted-foreground">
												{t("linkedPerformanceProfiles")}
											</p>
											{(exerciseLinks?.performance_profiles || []).length >
											0 ? (
												<div className="space-y-1">
													{exerciseLinks.performance_profiles.map((profile) => (
														<div
															key={profile.config_id}
															className="rounded-sm border border-border/70 px-2 py-1 text-xs"
														>
															<p className="font-medium truncate">
																{profile.domain_name}
															</p>
															<p className="text-muted-foreground">
																{profile.sport || t("notAvailable")} ·{" "}
																{profile.unit || t("notAvailable")}
															</p>
														</div>
													))}
												</div>
											) : (
												<p className="text-xs text-muted-foreground">
													{t("noPerformanceProfileLinks")}
												</p>
											)}
										</div>
									</div>
								)}
							</div>

							<div className="space-y-2">
								<Label className="label-tactical">{t("exerciseName")}</Label>
								<Input
									value={selectedExercise.name || ""}
									onChange={(e) =>
										setSelectedExercise({
											...selectedExercise,
											name: e.target.value,
										})
									}
									className="bg-secondary border-transparent rounded-sm"
								/>
							</div>
							<div className="space-y-2">
								<Label className="label-tactical">{t("description")}</Label>
								<Textarea
									value={selectedExercise.description || ""}
									onChange={(e) =>
										setSelectedExercise({
											...selectedExercise,
											description: e.target.value,
										})
									}
									className="bg-secondary border-transparent rounded-sm min-h-[80px]"
								/>
							</div>
							<div className="space-y-2">
								<Label className="label-tactical">{t("videoUrl")}</Label>
								<Input
									value={selectedExercise.video_url || ""}
									onChange={(e) =>
										setSelectedExercise({
											...selectedExercise,
											video_url: e.target.value,
										})
									}
									className="bg-secondary border-transparent rounded-sm"
								/>
							</div>
							<div className="space-y-2">
								<Label className="label-tactical">{t("category")}</Label>
								<Select
									value={normalizeCategoryValue(selectedExercise.category)}
									onValueChange={(value) =>
										setSelectedExercise({
											...selectedExercise,
											category: value,
										})
									}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										<SelectValue />
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										{EXERCISE_CATEGORIES.map((cat) => (
											<SelectItem key={cat.value} value={cat.value}>
												{getCategoryLabel(cat)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<div className="space-y-2">
								<Label className="label-tactical">{t("tags")}</Label>
								<Input
									value={selectedExercise.tags || ""}
									onChange={(e) =>
										setSelectedExercise({
											...selectedExercise,
											tags: e.target.value,
										})
									}
									className="bg-secondary border-transparent rounded-sm"
								/>
							</div>
							<div className="space-y-2">
								<Label className="label-tactical">
									{t("loadReferenceExercise")}
								</Label>
								<Select
									value={
										selectedExercise.load_reference_exercise_id ||
										"__current_exercise__"
									}
									onValueChange={(value) =>
										setSelectedExercise({
											...selectedExercise,
											...resolveLoadReferenceSelection(value),
										})
									}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										<SelectValue
											placeholder={t("currentExerciseLoadReference")}
										/>
									</SelectTrigger>
									<SelectContent className="bg-card border-border max-h-72">
										<SelectItem value="__current_exercise__">
											{t("currentExerciseLoadReference")}
										</SelectItem>
										<SelectItem value="__bodyweight__">
											{t("athleteBodyweightReference")}
										</SelectItem>
										{loadReferenceOptions.map((option) => (
											<SelectItem
												key={option.exercise_id}
												value={option.exercise_id}
											>
												{option.name || option.exercise_name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							{/* Image management */}
							{selectedExercise.image_url ? (
								<div className="space-y-2">
									<Label className="label-tactical">{t("currentImage")}</Label>
									<div className="relative">
										<img
											src={getImageUrl(selectedExercise.image_url)}
											alt="exercise"
											className="w-full h-40 object-cover rounded-sm"
										/>
										<Button
											variant="destructive"
											size="sm"
											className="absolute top-2 right-2 rounded-sm"
											onClick={() =>
												handleDeleteImage(selectedExercise.exercise_id)
											}
										>
											<Trash2 className="w-3 h-3 mr-1" />
											{t("removeImage")}
										</Button>
									</div>
								</div>
							) : (
								<div className="space-y-2">
									<Label className="label-tactical">{t("uploadImage")}</Label>
									<label className="flex items-center justify-center gap-2 p-4 border border-dashed border-border rounded-sm cursor-pointer hover:border-volt/50">
										<Upload className="w-5 h-5 text-muted-foreground" />
										<span className="text-sm text-muted-foreground">
											{isUploading ? t("uploading") : t("clickToUpload")}
										</span>
										<input
											type="file"
											accept="image/*"
											className="hidden"
											disabled={isUploading}
											onChange={(e) =>
												handleImageUpload(
													selectedExercise.exercise_id,
													e.target.files[0],
												)
											}
										/>
									</label>
								</div>
							)}
							{selectedExercise.video_url &&
								getYouTubeEmbedUrl(selectedExercise.video_url) && (
									<div className="space-y-2">
										<Label className="label-tactical">
											{t("videoPreview")}
										</Label>
										<div className="aspect-video rounded-sm overflow-hidden">
											<iframe
												src={getYouTubeEmbedUrl(selectedExercise.video_url)}
												title="Video"
												className="w-full h-full"
												allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
												allowFullScreen
											/>
										</div>
									</div>
								)}
							<Button
								type="submit"
								className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							>
								{t("saveChanges")}
							</Button>
						</form>
					)}
				</DialogContent>
			</Dialog>

			{/* Edit Conditioning Dialog */}
			<Dialog
				open={!!editingCondExercise}
				onOpenChange={(open) => {
					if (!open) {
						setEditingCondExercise(null);
						setNewConditioning(emptyConditioningTemplate());
					}
				}}
			>
				<DialogContent className="bg-card border-border max-w-lg max-h-[80vh] overflow-y-auto">
					<DialogDescription className="hidden" />
					<DialogHeader>
						<DialogTitle className="font-heading">
							{t("editConditioning")}
						</DialogTitle>
					</DialogHeader>
					<form onSubmit={handleUpdateConditioning} className="space-y-4">
						<ConditioningTemplateEditor
							value={newConditioning}
							setValue={setNewConditioning}
							isAdmin={user?.role === "admin"}
						/>
						<Button
							type="submit"
							className="w-full bg-volt hover:bg-volt/90 rounded-sm"
						>
							{t("saveChanges")}
						</Button>
					</form>
				</DialogContent>
			</Dialog>
		</div>
	);
}
